'use client';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceSubscriptionsOptimized } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';
import { Zap, Database, Video } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { FC } from 'react';

interface CreditBalanceProps {
  className?: string;
  compact?: boolean;
  statusOnly?: boolean;
  showPlanSuffix?: boolean;
}

const CreditBalance: FC<CreditBalanceProps> = ({
  className,
  compact = false,
  statusOnly = false,
  showPlanSuffix = false,
}) => {
  const workspaceContext = useWorkspace();
  const router = useRouter();

  const activeWorkspace = workspaceContext?.activeWorkspace;
  const hasWorkspaces = workspaceContext?.hasWorkspaces ?? false;

  const { data: subscriptionsData, isLoading } = useWorkspaceSubscriptionsOptimized({
    enabled: hasWorkspaces,
    workspaceId: activeWorkspace?.vendorid,
  });

  if (!workspaceContext) {
    return null;
  }

  if (!hasWorkspaces) {
    return null;
  }

  if (!activeWorkspace || isLoading) {
    return (
      <div className={cn('flex items-center gap-2 px-3 py-2', className)}>
        <div className='animate-pulse'>
          <div className='h-4 w-16 bg-gray-200 rounded'></div>
        </div>
      </div>
    );
  }

  const handleNavigateToBilling = () => {
    router.push('/settings?tab=billing-and-usage');
  };

  const activeSub = subscriptionsData?.subscriptions?.find(
    (sub) => sub.status === 'active' || sub.status === 'trial',
  );
  const subStatus = activeSub?.status ?? 'inactive';
  const subPlan = activeSub?.plan ?? null;
  const isEnterprise = subPlan === 'enterprise';
  const hasEnterpriseAiUnlimited = activeSub?.enterprise_ai_unlimited === true;
  const aiBalance = activeSub?.ai_credit_balance ?? 0;
  const avatarBalance = activeSub?.video_avatar_credit_balance ?? 0;
  const avatarMinutes = Math.floor(avatarBalance / 2);
  const formatAiBalance = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
        ? `${(n / 1_000).toFixed(0)}K`
        : n.toLocaleString();

  const enrBalance = subscriptionsData?.credits?.enrichment_credit_balance ?? 0;

  const statusLabel =
    subStatus === 'trial'
      ? 'Trial'
      : subStatus === 'active'
        ? isEnterprise
          ? 'Enterprise'
          : 'Active'
        : 'Inactive';
  const compactStatusLabel = showPlanSuffix && isEnterprise ? `${statusLabel} plan` : statusLabel;
  const statusColor =
    subStatus === 'trial'
      ? 'text-orange-700'
      : subStatus === 'active'
        ? 'text-emerald-700'
        : 'text-gray-500';
  const dotColor =
    subStatus === 'trial'
      ? 'bg-orange-500'
      : subStatus === 'active'
        ? 'bg-emerald-500'
        : 'bg-gray-400';

  if (compact) {
    return (
      <div className={cn('flex items-center gap-3 px-3 py-2', className)}>
        <div className='flex items-center gap-1.5'>
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className={`text-xs font-medium ${statusColor}`}>{compactStatusLabel}</span>
        </div>
        {!statusOnly && !isEnterprise && (
          <>
            <div className='w-px h-4 bg-gray-300' />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5'>
                    <Database className='h-3.5 w-3.5 text-emerald-600' />
                    <span className='text-xs font-medium text-emerald-900'>
                      {enrBalance.toLocaleString()}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-sm'>
                    Enrichment Credits: {enrBalance.toLocaleString()} available
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className='w-px h-4 bg-gray-300' />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5'>
                    <Zap className='h-3.5 w-3.5 text-purple-600' />
                    <span className='text-xs font-medium text-purple-900'>
                      {formatAiBalance(aiBalance)}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-sm'>AI Credits: {aiBalance.toLocaleString()} available</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className='w-px h-4 bg-gray-300' />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5'>
                    <Video className='h-3.5 w-3.5 text-cyan-600' />
                    <span className='text-xs font-medium text-cyan-900'>{avatarBalance}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-sm'>
                    Video Avatar Credits: {avatarBalance} ({avatarMinutes} min)
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow',
        className,
      )}
    >
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold text-gray-800'>Credits</h3>
        <button
          onClick={handleNavigateToBilling}
          className='text-xs text-blue-600 hover:text-blue-700 font-medium'
        >
          Details
        </button>
      </div>
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
          <span className={`text-sm font-semibold ${statusColor}`}>{statusLabel}</span>
          {subPlan && subPlan !== 'trial' && (
            <span className='text-xs text-gray-500 capitalize'>· {subPlan}</span>
          )}
        </div>
        {!isEnterprise && (
          <div>
            <div className='flex items-center justify-between mb-1'>
              <div className='flex items-center gap-1.5'>
                <Database className='h-3.5 w-3.5 text-emerald-600' />
                <span className='text-xs text-gray-600'>Enrichment</span>
              </div>
              <span className='text-xs font-medium text-gray-800'>
                {enrBalance.toLocaleString()} left
              </span>
            </div>
            {enrBalance <= 100 && (
              <button
                onClick={handleNavigateToBilling}
                className='w-full py-1.5 px-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-xs font-medium text-emerald-700'
              >
                {enrBalance === 0 ? 'Buy Enrichment Credits' : 'Running Low — Buy More'}
              </button>
            )}
          </div>
        )}
        <div>
          <div className='flex items-center justify-between mb-1'>
            <div className='flex items-center gap-1.5'>
              <Zap className='h-3.5 w-3.5 text-purple-600' />
              <span className='text-xs text-gray-600'>AI Credits</span>
            </div>
            <span className='text-xs font-medium text-gray-800'>
              {isEnterprise && hasEnterpriseAiUnlimited
                ? 'Unlimited'
                : `${formatAiBalance(aiBalance)} left`}
            </span>
          </div>
          {(!isEnterprise || !hasEnterpriseAiUnlimited) &&
            aiBalance <= 1_000_000 &&
            aiBalance >= 0 && (
              <button
                onClick={handleNavigateToBilling}
                className='w-full py-1.5 px-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-xs font-medium text-purple-700'
              >
                {aiBalance === 0 ? 'Buy AI Credits' : 'Running Low — Buy More'}
              </button>
            )}
        </div>
        <div>
          <div className='flex items-center justify-between mb-1'>
            <div className='flex items-center gap-1.5'>
              <Video className='h-3.5 w-3.5 text-cyan-600' />
              <span className='text-xs text-gray-600'>Video Avatar</span>
            </div>
            <span className='text-xs font-medium text-gray-800'>
              {avatarBalance} ({avatarMinutes} min)
            </span>
          </div>
          {avatarBalance <= 6 && (
            <button
              onClick={handleNavigateToBilling}
              className='w-full py-1.5 px-3 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-xs font-medium text-cyan-700'
            >
              {avatarBalance === 0 ? 'Buy Video Avatar Credits' : 'Running Low — Buy More'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditBalance;
